/**
 * Where the signed-in user's tokens are kept.
 *
 * Security note, stated plainly: any browser storage can be read by injected
 * JavaScript. The intended end state is for the refresh token to live in an
 * httpOnly cookie that scripts cannot read, which is a backend change. The dev
 * server already proxies the API onto the portal's own origin, so moving to
 * cookies later needs no change here.
 *
 * `sessionStorage` is used rather than `localStorage` so tokens die when the tab
 * closes, which suits an administration tool.
 */

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

export interface TokenStore {
  get: () => Tokens | null;
  set: (tokens: Tokens) => void;
  clear: () => void;
}

const STORAGE_KEY = 'activistarc.portal.tokens';

/** Backed by sessionStorage, falling back to memory when storage is unavailable. */
export function createSessionTokenStore(): TokenStore {
  return {
    get() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        if (
          typeof parsed === 'object' &&
          parsed !== null &&
          typeof (parsed as Tokens).accessToken === 'string' &&
          typeof (parsed as Tokens).refreshToken === 'string'
        ) {
          return parsed as Tokens;
        }
        return null;
      } catch {
        // Private browsing, disabled storage, or corrupt contents.
        return null;
      }
    },
    set(tokens) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
      } catch {
        // Ignore: the request still carries the token in memory for this page.
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // Nothing to do.
      }
    },
  };
}

/** Used by tests, where sessionStorage state would leak between cases. */
export function createMemoryTokenStore(): TokenStore {
  let tokens: Tokens | null = null;
  return {
    get: () => tokens,
    set: (next) => {
      tokens = next;
    },
    clear: () => {
      tokens = null;
    },
  };
}

export const tokenStore: TokenStore = createSessionTokenStore();
